import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { VIPSection } from "@/components/seating/VIPSection";
import { StandingSection } from "@/components/seating/StandingSection";
import { VIPSeatingTemplate } from "@/components/seating/VIPSeatingTemplate";
import { SeatingHeader } from "@/components/seating/SeatingHeader";
import { PaymentSection } from "@/components/seating/PaymentSection";
import { Event } from "@/lib/types";
import { useIsMobile } from "@/hooks/use-mobile";

// Google Analytics e-commerce tracking
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

const SeatingPlan = () => {
  const { seoUrl } = useParams<{ seoUrl: string }>();
  const navigate = useNavigate();
  const [selectedSeats, setSelectedSeats] = useState<string[]>([]);
  const [vipTicketCount, setVipTicketCount] = useState(0);
  const [standingTickets, setStandingTickets] = useState(0);
  const [earlyBirdTickets, setEarlyBirdTickets] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const isMobile = useIsMobile();

  const { data: event, isLoading, error } = useQuery({
    queryKey: ["event", seoUrl],
    queryFn: async () => {
      console.log("Fetching event with SEO URL:", seoUrl);
      
      if (!seoUrl) {
        console.error("No event SEO URL provided");
        throw new Error("No event SEO URL provided");
      }
      
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("seo_url", seoUrl)
        .maybeSingle();

      console.log("Supabase response:", { data, error });
      
      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }
      if (!data) throw new Error("Event not found");
      
      return data as Event;
    },
    enabled: !!seoUrl,
    meta: {
      onError: (error: Error) => {
        console.error("Error fetching event:", error);
        toast.error("Failed to load event details");
      }
    }
  });

  // Check if event is past and redirect if necessary
  useEffect(() => {
    if (event && seoUrl) {
      const eventDate = new Date(event.date);
      const currentTime = new Date();
      const fiveHoursInMs = 5 * 60 * 60 * 1000; // 5 hours in milliseconds
      const isEventPast = currentTime.getTime() - eventDate.getTime() > fiveHoursInMs;
      
      if (isEventPast) {
        console.log("Event is past, redirecting to main event page");
        toast.error("This event has already concluded");
        navigate(`/events/${seoUrl}`, { replace: true });
      }
    }
  }, [event, seoUrl, navigate]);

  // Restore checkout data from localStorage if available
  useEffect(() => {
    const savedData = localStorage.getItem("checkoutData");
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        
        // Check if the data is for the current event and not too old (24 hours)
        const isValid = 
          parsedData.eventId === event?.id && 
          new Date().getTime() - new Date(parsedData.timestamp).getTime() < 24 * 60 * 60 * 1000;

        if (isValid) {
          console.log("Restoring saved checkout data:", parsedData);
          
          // Restore ticket selections
          if (parsedData.tickets) {
            setSelectedSeats(parsedData.tickets.vipSeats || []);
            setStandingTickets(parsedData.tickets.standingTickets || 0);
          }
          
          toast.success("Previous selections restored");
        } else {
          // Clear invalid or expired data
          localStorage.removeItem("checkoutData");
        }
      } catch (error) {
        console.error("Error restoring checkout data:", error);
        localStorage.removeItem("checkoutData");
      }
    }
  }, [event]);

  // E-commerce tracking functions
  const trackBeginCheckout = useCallback((event: Event, selectedSeats: string[], vipTicketCount: number, standingTickets: number, earlyBirdTickets: number) => {
    if (typeof window !== 'undefined' && window.gtag) {
      const items = [];
      
      // Add VIP seats/tickets
      const vipQuantity = event.template_type === 'no_seating' ? selectedSeats.length : vipTicketCount;
      if (vipQuantity > 0) {
        items.push({
          item_id: `vip_${event.id}`,
          item_name: `VIP Ticket - ${event.title}`,
          item_category: 'Tickets',
          item_brand: 'Slay Events',
          price: event.price_vip,
          quantity: vipQuantity
        });
      }
      
      // Add standing tickets
      if (standingTickets > 0) {
        items.push({
          item_id: `standing_${event.id}`,
          item_name: `General Admission - ${event.title}`,
          item_category: 'Tickets',
          item_brand: 'Slay Events',
          price: event.price_general_admission,
          quantity: standingTickets
        });
      }
      
      // Add early bird tickets
      if (earlyBirdTickets > 0) {
        items.push({
          item_id: `early_bird_${event.id}`,
          item_name: `Early Bird - ${event.title}`,
          item_category: 'Tickets',
          item_brand: 'Slay Events',
          price: event.early_bird_price || 0,
          quantity: earlyBirdTickets
        });
      }
      
      const totalValue = (vipQuantity * event.price_vip) + 
                        (standingTickets * event.price_general_admission) + 
                        (earlyBirdTickets * (event.early_bird_price || 0));
      
      window.gtag('event', 'begin_checkout', {
        currency: event.currency || 'AUD',
        value: totalValue,
        items: items
      });
      
      console.log('E-commerce tracking: begin_checkout', { value: totalValue, items });
    }
  }, []);

  const trackAddToCart = useCallback((event: Event, itemType: string, quantity: number, price: number) => {
    if (typeof window !== 'undefined' && window.gtag) {
      const item = {
        item_id: `${itemType}_${event.id}`,
        item_name: `${itemType === 'vip' ? 'VIP' : itemType === 'standing' ? 'General Admission' : 'Early Bird'} - ${event.title}`,
        item_category: 'Tickets',
        item_brand: 'Slay Events',
        price: price,
        quantity: quantity
      };
      
      window.gtag('event', 'add_to_cart', {
        currency: event.currency || 'AUD',
        value: price * quantity,
        items: [item]
      });
      
      console.log('E-commerce tracking: add_to_cart', { item, value: price * quantity });
    }
  }, []);

  const handleSeatClick = useCallback((seatId: string) => {
    setSelectedSeats(prev => {
      const newSeats = prev.includes(seatId) 
        ? prev.filter(seat => seat !== seatId)
        : [...prev, seatId];
      
      // Track add to cart when seat is selected (not deselected)
      if (!prev.includes(seatId) && event) {
        trackAddToCart(event, 'vip', 1, event.price_vip);
      }
      
      return newSeats;
    });
  }, [event, trackAddToCart]);

  const handleVipTicketChange = useCallback((increment: boolean) => {
    if (!event) return;

    setVipTicketCount(prev => {
      if (increment) {
        if (prev >= (event.quantity_vip || 0)) {
          toast.error("No more VIP tickets available");
          return prev;
        }
        // Track add to cart for VIP ticket
        trackAddToCart(event, 'vip', 1, event.price_vip);
        return prev + 1;
      } else {
        return Math.max(0, prev - 1);
      }
    });
  }, [event, trackAddToCart]);

  const handleStandingTicketChange = useCallback((increment: boolean) => {
    if (!event) return;

    if (increment) {
      if (standingTickets >= (event.quantity_standing || 0)) {
        toast.error("No more standing tickets available");
        return;
      }
      // Track add to cart for standing ticket
      trackAddToCart(event, 'standing', 1, event.price_general_admission);
      setStandingTickets(prev => prev + 1);
    } else {
      setStandingTickets(prev => Math.max(0, prev - 1));
    }
  }, [event, standingTickets, trackAddToCart]);

  const handleEarlyBirdTicketChange = useCallback((increment: boolean) => {
    if (!event) return;

    if (increment) {
      if (earlyBirdTickets >= (event.quantity_early_bird || 0)) {
        toast.error("No more early bird tickets available");
        return;
      }
      // Track add to cart for early bird ticket
      trackAddToCart(event, 'early_bird', 1, event.early_bird_price || 0);
      setEarlyBirdTickets(prev => prev + 1);
    } else {
      setEarlyBirdTickets(prev => Math.max(0, prev - 1));
    }
  }, [event, earlyBirdTickets, trackAddToCart]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 pt-28 pb-12">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !event || !seoUrl) {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 pt-28 pb-12">
          <div className="text-white text-center">
            <h2 className="text-2xl font-bold mb-4">
              {error ? "Error Loading Event" : "Event Not Found"}
            </h2>
            {error && <p>{error.message}</p>}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <Header />
      <main className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-12">
        <div className="space-y-6 sm:space-y-8">
          <SeatingHeader seoUrl={seoUrl} eventTitle={event.title} />

          <div className="bg-gray-800 p-4 sm:p-6 lg:p-8 rounded-lg">
            {event?.template_type === 'no_seating' ? (
              <>
                <div className="bg-gray-700 p-4 sm:p-6 rounded-lg mb-6 sm:mb-8">
                  <div className="flex flex-col space-y-4">
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                      <div className="space-y-2">
                        <h3 className="text-xl sm:text-2xl font-bold text-white">VIP: Meet and Greet</h3>
                        {event.ticket_description_vip && (
                          <p className="text-sm sm:text-base text-gray-400 italic">{event.ticket_description_vip}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row justify-between gap-6 lg:gap-24">
                  <div className="flex-1 flex justify-center">
                    <VIPSection
                      sectionNumber={1}
                      selectedSeats={selectedSeats}
                      onSeatClick={handleSeatClick}
                      maxSeats={event.quantity_vip || 0}
                    />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <VIPSection
                      sectionNumber={2}
                      selectedSeats={selectedSeats}
                      onSeatClick={handleSeatClick}
                      maxSeats={event.quantity_vip || 0}
                    />
                  </div>
                </div>
                
                <StandingSection
                  event={event}
                  standingTickets={standingTickets}
                  maxStandingTickets={event.quantity_standing || 0}
                  earlyBirdTickets={earlyBirdTickets}
                  maxEarlyBirdTickets={event.quantity_early_bird || 0}
                  onStandingTicketChange={handleStandingTicketChange}
                  onEarlyBirdTicketChange={handleEarlyBirdTicketChange}
                />
              </>
            ) : (
              <VIPSeatingTemplate
                event={event}
                vipTicketCount={vipTicketCount}
                standingTickets={standingTickets}
                earlyBirdTickets={earlyBirdTickets}
                onVipTicketChange={handleVipTicketChange}
                onStandingTicketChange={handleStandingTicketChange}
                onEarlyBirdTicketChange={handleEarlyBirdTicketChange}
              />
            )}
          </div>

          <PaymentSection
            event={event}
            seoUrl={seoUrl}
            selectedSeats={event?.template_type === 'no_seating' ? selectedSeats : []}
            vipTicketCount={event?.template_type === 'vip_seating' ? vipTicketCount : 0}
            standingTickets={standingTickets}
            earlyBirdTickets={earlyBirdTickets}
            discountPercentage={discountPercentage}
            setDiscountPercentage={setDiscountPercentage}
            onBeginCheckout={() => trackBeginCheckout(event, selectedSeats, vipTicketCount, standingTickets, earlyBirdTickets)}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SeatingPlan;
